import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Brain,
  Sparkles,
  Target,
  ArrowRight,
  Users,
  CheckCircle,
  TrendingUp,
  Network,
  Lightbulb,
  BarChart3,
  Calendar,
  XCircle,
  AlertTriangle,
  Heart,
  Zap,
  Award,
  MessageSquare,
  ChevronRight,
  Eye,
  Bot,
  Gauge,
  FileText,
  Shield,
  Map,
  FlaskConical,
  Radio,
  Activity,
  Smartphone,
  CheckSquare,
  Check,
  X,
  Crown,
  Star } from
"lucide-react";
import { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { motion } from "framer-motion";
import { Capacitor } from "@capacitor/core";
import logoFullWhite from "@/assets/logo-full-white.svg";
import logoFullBlack from "@/assets/logo-full-black.svg";
import {
  TIER_LIMITS,
  TIER_PRICING,
  TIER_NAMES,
  formatCurrency,
  type SubscriptionTier } from
"@/lib/subscriptionFeatures";
import { supabase } from "@/integrations/supabase/client";
import { UpgradeRequestDialog } from "@/components/admin/UpgradeRequestDialog";
import { toast } from "sonner";

const Welcome = () => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [userCompany, setUserCompany] = useState<{id: string;tier: SubscriptionTier;} | null>(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [selectedUpgradeTier, setSelectedUpgradeTier] = useState<SubscriptionTier>('professional');

  useEffect(() => {
    // Native apps also use the Welcome page now
    setMounted(true);

    // Check if user is logged in and has a company
    const checkUserCompany = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase.
        from('worker_profiles').
        select('company_id, companies(id, subscription_tier)').
        eq('user_id', user.id).
        maybeSingle();

        if (profile?.company_id && profile.companies) {
          setUserCompany({
            id: profile.company_id,
            tier: (profile.companies as any).subscription_tier as SubscriptionTier
          });
        }
      }
    };
    checkUserCompany();
  }, [navigate]);

  const handlePricingAction = (tier: SubscriptionTier) => {
    if (userCompany) {
      // Logged-in user with existing company → open upgrade request dialog
      if (tier === userCompany.tier) {
        toast.info(`Anda sudah menggunakan paket ${TIER_NAMES[tier]}`);
        return;
      }
      const tierOrder = { free: 0, professional: 1, enterprise: 2, enterprise_plus: 3 };
      if (tierOrder[tier] < tierOrder[userCompany.tier]) {
        toast.info('Hubungi tim kami untuk downgrade paket');
        return;
      }
      setSelectedUpgradeTier(tier);
      setShowUpgradeDialog(true);
    } else {
      // New user → company signup
      navigate(`/company-signup?tier=${tier}`);
    }
  };

  const handleRequestDemo = () => {
    window.open("https://calendly.com/sorinsafety-id/30min", "_blank");
  };

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 pt-safe backdrop-blur-md bg-background/80 border-b border-border/50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={logoFullBlack} alt="SORIN" className="h-8 dark:hidden" />
            <img src={logoFullWhite} alt="SORIN" className="h-8 hidden dark:block" />
          </div>

          <nav className="hidden md:flex items-center gap-8">
            <a href="#problem" className="text-sm font-medium hover:text-primary transition">
              The Problem
            </a>
            <a href="#how-it-works" className="text-sm font-medium hover:text-primary transition">
              How It Works
            </a>
            <a href="#features" className="text-sm font-medium hover:text-primary transition">
              Features
            </a>
            <a href="#pricing" className="text-sm font-medium hover:text-primary transition">
              Pricing
            </a>
            <a href="#science" className="text-sm font-medium hover:text-primary transition">
              The Science
            </a>
            <button
              onClick={() => navigate("/help")}
              className="text-sm font-medium hover:text-primary transition">
              
              Help
            </button>
          </nav>

          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin/login")}
              className="text-sm font-medium hover:opacity-70 transition">
              
              Admin
            </button>
            <Button size="sm" onClick={() => navigate("/login")}>
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-success/10 rounded-full blur-[100px]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={mounted ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="space-y-8">
              
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                <Brain className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">Behavioral Safety Intelligence Platform</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold leading-tight">
                <span className="text-foreground">Stop </span>
                <span className="bg-gradient-to-r from-destructive to-destructive/70 bg-clip-text text-transparent">
                  Reacting
                </span>
                <span className="text-foreground"> to Incidents.</span>
                <br />
                <span className="text-foreground">Start </span>
                <span className="bg-gradient-to-r from-primary via-accent to-success bg-clip-text text-transparent">
                  Predicting
                </span>
                <span className="text-foreground"> Them.</span>
              </h1>

              <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                SORIN uses <strong>peer-reviewed behavioral science</strong> and <strong>AI</strong> to identify at-risk
                workers <em>before</em> they have accidents. Reduce TRIR, predict risk, and change behavior at scale.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  size="lg"
                  className="h-14 px-8 text-base bg-gradient-to-r from-primary to-primary/80 hover:opacity-90 shadow-lg shadow-primary/25"
                  onClick={handleRequestDemo}>
                  
                  <Calendar className="mr-2 h-5 w-5" />
                  Request a Demo
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-8 text-base"
                  onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}>
                  
                  See How It Works
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </motion.div>
          </div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={mounted ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-20 max-w-4xl mx-auto">
            
            <StatsCard label="Risk Prediction Accuracy" value="73%" icon={<Target className="h-5 w-5" />} />
            <StatsCard label="Behavior Change Rate" value="65%" icon={<TrendingUp className="h-5 w-5" />} />
            <StatsCard label="Incident Reduction" value="45%" icon={<Shield className="h-5 w-5" />} />
            <StatsCard label="Worker Engagement" value="89%" icon={<Heart className="h-5 w-5" />} />
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section id="problem" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-destructive/10 border border-destructive/20 mb-6">
              <XCircle className="h-4 w-4 text-destructive" />
              <span className="text-sm font-medium text-destructive">The Industry Problem</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Traditional Safety Systems Fail</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Most safety software just digitizes paperwork. They measure compliance, not behavior. 
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="p-8 rounded-2xl bg-card border-2 border-destructive/20">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 rounded-xl bg-destructive/10">
                    <XCircle className="h-6 w-6 text-destructive" />
                  </div>
                  <h3 className="text-xl font-bold text-destructive">Traditional Systems</h3>
                </div>
                <ul className="space-y-4">
                  <ComparisonItem negative text="React AFTER accidents happen" />
                  <ComparisonItem negative text="Focus on paperwork & compliance" />
                  <ComparisonItem negative text="Blame-based culture" />
                  <ComparisonItem negative text="Generic training for everyone" />
                  <ComparisonItem negative text="Measure incident counts (lagging)" />
                  <ComparisonItem negative text="Workers fear reporting" />
                  <ComparisonItem negative text="Data silos, no insights" />
                </ul>
              </div>

              <div className="p-8 rounded-2xl bg-card border-2 border-primary/20 shadow-lg shadow-primary/5">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 rounded-xl bg-primary/10">
                    <CheckCircle className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-primary">SORIN Approach</h3>
                </div>
                <ul className="space-y-4">
                  <ComparisonItem text="PREDICT risks before accidents" />
                  <ComparisonItem text="Focus on behavior change" />
                  <ComparisonItem text="Just Culture - no blame" />
                  <ComparisonItem text="Personalized interventions" />
                  <ComparisonItem text="Predict risk scores (leading)" />
                  <ComparisonItem text="Workers trust the system" />
                  <ComparisonItem text="AI-powered insights & recommendations" />
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How SORIN Works - Signal > Diagnose > Act > Result */}
      <section id="how-it-works" className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Zap className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">The SORIN Method</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How SORIN Works</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From signal to result — a complete cycle of predictive safety intelligence.
            </p>
          </div>

          {/* Static flow overview */}
          <div className="max-w-5xl mx-auto mb-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6">
              {[
              { step: 1, label: "Signal", subtitle: "Capture", color: "bg-blue-100 text-blue-600", icon: <Radio className="h-7 w-7" />, desc: "AI-powered hazard reporting with photo analysis, voice input, and evidence quality scoring. Workers report naturally; SORIN extracts the intelligence." },
              { step: 2, label: "Diagnose", subtitle: "Predict", color: "bg-stone-100 text-stone-700", icon: <Brain className="h-7 w-7" />, desc: "Psychometric risk profiling, social network mapping, and friction point detection identify who's at risk before incidents occur." },
              { step: 3, label: "Act", subtitle: "Intervene", color: "bg-amber-100 text-amber-600", icon: <Zap className="h-7 w-7" />, desc: "MDP-powered recommendations deliver the right intervention to the right worker at the right time — nudges, quests, or supervisor escalation." },
              { step: 4, label: "Result", subtitle: "Validate", color: "bg-emerald-100 text-emerald-600", icon: <TrendingUp className="h-7 w-7" />, desc: "30-day effectiveness tracking, A/B testing, and ROI measurement prove what works and continuously improve outcomes." }].
              map((item) =>
              <div key={item.step} className="flex flex-col items-center text-center">
                  <div className="relative mb-5">
                    <div className={`p-5 rounded-2xl ${item.color}`}>
                      {item.icon}
                    </div>
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-foreground text-background text-xs font-bold flex items-center justify-center shadow-md">
                      {item.step}
                    </span>
                  </div>
                  <h4 className="font-bold text-lg mb-1">{item.label}</h4>
                  <p className="text-sm font-medium text-primary mb-3">{item.subtitle}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              )}
            </div>
          </div>

          {/* Interactive demo tabs */}
          <div className="max-w-5xl mx-auto">
            <Tabs defaultValue="worker-app" className="w-full">
              <TabsList className="w-full flex h-auto bg-muted/50 rounded-xl p-1.5 gap-1">
                {[
                { value: "worker-app", label: "Worker App", icon: <Smartphone className="h-4 w-4" /> },
                { value: "signal", label: "Signal", icon: <Radio className="h-4 w-4" /> },
                { value: "diagnose", label: "Diagnose", icon: <Brain className="h-4 w-4" /> },
                { value: "act", label: "Act", icon: <Zap className="h-4 w-4" /> },
                { value: "verify", label: "Verify", icon: <TrendingUp className="h-4 w-4" /> }].
                map((tab) =>
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-lg text-xs sm:text-sm font-medium data-[state=active]:bg-background data-[state=active]:shadow-md data-[state=active]:text-primary transition-all">
                  
                    {tab.icon}
                    <span className="hidden sm:inline">{tab.label}</span>
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Worker App - portrait aspect ratio */}
              <TabsContent value="worker-app" className="mt-6">
                <div className="flex flex-col items-center">
                  <div className="relative w-full max-w-[400px]" style={{ aspectRatio: "0.45" }}>
                    <iframe
                      src="https://app.supademo.com/embed/cmlgc71f10z4a60aevsmfrh7x?embed_v=2&utm_source=embed"
                      loading="lazy"
                      title="SORIN Worker App Demo"
                      allow="clipboard-write"
                      frameBorder="0"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full rounded-xl" />
                    
                  </div>
                  <p className="text-muted-foreground text-center mt-4 max-w-lg text-sm">
                    Workers report hazards naturally through AI-powered conversation — voice, text, or photo. SORIN extracts structured intelligence automatically.
                  </p>
                </div>
              </TabsContent>

              {/* Signal - landscape */}
              <TabsContent value="signal" className="mt-6">
                <div className="flex flex-col items-center">
                  <div className="relative w-full" style={{ aspectRatio: "1.92" }}>
                    <iframe
                      src="https://app.supademo.com/embed/cmlhh17q50n6p5351brch8l2e?embed_v=2&utm_source=embed"
                      loading="lazy"
                      title="SORIN Signal Demo"
                      allow="clipboard-write"
                      frameBorder="0"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full rounded-xl" />
                    
                  </div>
                  <p className="text-muted-foreground text-center mt-4 max-w-lg text-sm">
                    Signals are captured from peer reports, check-ins, psychometric assessments, and network connections — building a complete risk picture.
                  </p>
                </div>
              </TabsContent>

              {/* Diagnose - landscape */}
              <TabsContent value="diagnose" className="mt-6">
                <div className="flex flex-col items-center">
                  <div className="relative w-full" style={{ aspectRatio: "1.92" }}>
                    <iframe
                      src="https://app.supademo.com/embed/cmlhqfe4y0xwr53511xg0mwhw?embed_v=2&utm_source=embed"
                      loading="lazy"
                      title="SORIN Diagnose Demo"
                      allow="clipboard-write"
                      frameBorder="0"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full rounded-xl" />
                    
                  </div>
                  <p className="text-muted-foreground text-center mt-4 max-w-lg text-sm">
                    The Unified Diagnostic Engine aggregates friction mapping, cluster analysis, and risk profiling into prioritized recommendations.
                  </p>
                </div>
              </TabsContent>

              {/* Act - landscape embed */}
              <TabsContent value="act" className="mt-6">
                <div className="flex flex-col items-center">
                  <div className="relative w-full" style={{ aspectRatio: "1.93" }}>
                    <iframe
                      src="https://app.supademo.com/embed/cmlixcdzf3b1y5351c0xfax9a?embed_v=2&utm_source=embed"
                      loading="lazy"
                      title="Act Demo"
                      allow="clipboard-write"
                      frameBorder="0"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full" />
                    
                  </div>
                  <p className="mt-4 text-sm text-muted-foreground text-center max-w-2xl">
                    See how SORIN delivers the right intervention — nudges, quests, or supervisor escalation — to the right worker at the right time.
                  </p>
                </div>
              </TabsContent>

              {/* Verify - placeholder */}
              <TabsContent value="verify" className="mt-6">
                <div className="flex flex-col items-center">
                  <div className="w-full max-w-2xl rounded-2xl border-2 border-dashed border-border bg-muted/30 p-12 flex flex-col items-center justify-center gap-4 min-h-[300px]">
                    <div className="p-4 rounded-2xl bg-success/10">
                      <TrendingUp className="h-10 w-10 text-success" />
                    </div>
                    <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Coming Soon</span>
                    <p className="text-muted-foreground text-center max-w-md text-sm">
                      Track 30-day effectiveness, A/B test interventions, and measure ROI — proving what works and continuously improving outcomes.
                    </p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 mb-6">
              <Sparkles className="h-4 w-4 text-accent-foreground" />
              <span className="text-sm font-medium">Platform Features</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Complete Safety Intelligence Platform</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Every feature designed to predict risk, change behavior, and create a culture of safety.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <FeatureCategory
                icon={<MessageSquare className="h-8 w-8" />}
                title="Worker Experience"
                features={[
                { icon: <Bot />, name: "AI Conversational Reporting", desc: "Natural voice/text hazard reports with auto-extraction" },
                { icon: <Award />, name: "Gamified Safety", desc: "XP, badges, streaks, team leaderboards" },
                { icon: <Target />, name: "Personalized Quests", desc: "Safety challenges matched to your profile" },
                { icon: <Eye />, name: "Pre-Shift Check-in", desc: "Daily task and location mapping" },
                { icon: <Users />, name: "Peer Corroboration", desc: "Quality validation through colleague input" }]
                } />
              
              <FeatureCategory
                icon={<BarChart3 className="h-8 w-8" />}
                title="Intelligence Layer"
                features={[
                { icon: <Gauge />, name: "Risk Pulse Dashboard", desc: "Real-time organizational risk status" },
                { icon: <Activity />, name: "Cluster Diagnostics", desc: "Team-level risk analysis" },
                { icon: <Map />, name: "Friction Mapping", desc: "Identify systemic hazard patterns" },
                { icon: <Network />, name: "Network Visualization", desc: "See how behavior spreads through teams" },
                { icon: <Users />, name: "Worker Diagnostics", desc: "MDP safety states & trajectories" }]
                } />
              
              <FeatureCategory
                icon={<Brain className="h-8 w-8" />}
                title="AI-Powered Actions"
                features={[
                { icon: <Bot />, name: "Safety Copilot", desc: "Ask anything about your safety data" },
                { icon: <Zap />, name: "MDP Recommendations", desc: "Optimal intervention matching" },
                { icon: <Lightbulb />, name: "Auto Interventions", desc: "Triggered based on risk signals" },
                { icon: <FlaskConical />, name: "Experiment Platform", desc: "A/B test intervention effectiveness" },
                { icon: <CheckSquare />, name: "Supervisor Hub", desc: "Escalation management & tracking" }]
                } />
              
            </div>
          </div>
        </div>
      </section>

      {/* Just Culture Section */}
      <section id="just-culture" className="py-24 bg-gradient-to-br from-primary/5 via-background to-success/5">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-success/10 border border-success/20 mb-6">
                <Heart className="h-4 w-4 text-success" />
                <span className="text-sm font-medium text-success">Our Philosophy</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Just Culture: Safety Without Blame</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Workers don't report hazards when they fear punishment. SORIN creates a psychologically safe environment
                where reporting is encouraged and workers feel supported, not surveilled.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <JustCultureCard
                icon={<Shield className="h-8 w-8" />}
                title="Non-Punitive Reporting"
                description="Workers never receive 'you were reported' alerts. No personal risk scores visible to workers. All feedback is framed as system improvement, not personal blame." />
              
              <JustCultureCard
                icon={<MessageSquare className="h-8 w-8" />}
                title="Supportive Language"
                description="AI conversations start with 'This is for learning, not punishment.' Supervisors 'confirm hazards exist' rather than 'verify if report is true.'" />
              
              <JustCultureCard
                icon={<Award className="h-8 w-8" />}
                title="Positive Reinforcement Only"
                description="No XP penalties, ever. Workers earn rewards for reporting, learning, and helping teammates. Team-based leaderboards, not individual competition." />
              
              <JustCultureCard
                icon={<Users className="h-8 w-8" />}
                title="Contextual Nudges"
                description="Instead of 'you violated X,' workers receive helpful reminders at shift start: 'Reminder: welding area requires face shield today.'" />
              
            </div>

            <div className="p-8 rounded-2xl bg-card border border-border/50 text-center">
              <p className="text-lg font-medium mb-2">
                "The goal is not to catch people doing wrong. It's to help everyone do right."
              </p>
              <p className="text-sm text-muted-foreground">— SORIN Just Culture Philosophy</p>
            </div>
          </div>
        </div>
      </section>

      {/* Science Section - Simplified */}
      <section id="science" className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <FlaskConical className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">Research-Backed Intelligence</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">The Science Behind SORIN</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Built on decades of peer-reviewed behavioral science research. Every feature is grounded in empirically validated theories.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <ScienceCard
                icon={<Brain className="h-10 w-10" />}
                title="Individual Risk Profiling"
                description="Validated psychometric assessments (UPPS-P, DOSPERT, Delay Discounting) identify workers prone to risk-taking before incidents occur."
                stat="2.4x"
                statLabel="more predictive than incident history" />
              
              <ScienceCard
                icon={<Network className="h-10 w-10" />}
                title="Social Contagion Modeling"
                description="Based on Damon Centola's 'How Behavior Spreads' — we map how risky and safe behaviors propagate through your workplace social network."
                stat="4x"
                statLabel="more likely to adopt peer behavior" />
              
              <ScienceCard
                icon={<Lightbulb className="h-10 w-10" />}
                title="Personalized Intervention"
                description="Match intervention style to individual psychology. High impulsivity workers need short messages; low risk perception workers need consequence visuals."
                stat="73%"
                statLabel="behavior change vs 23% generic" />
              
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Crown className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">Pricing Plans</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Choose Your Safety Intelligence Plan</h2>
            <p className="text-lg font-semibold text-primary mb-3">For companies. Free for your workers.</p>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Your company subscribes once — every worker on your team gets the mobile app, WhatsApp bot, and safety tools at no extra cost. Start free and scale as you grow.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            {/* Pricing Cards */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <PricingCard
                tier="free"
                isPopular={false}
                onSignup={() => handlePricingAction("free")}
                isCurrentTier={userCompany?.tier === "free"} />
              
              <PricingCard
                tier="professional"
                isPopular={true}
                onSignup={() => handlePricingAction("professional")}
                isCurrentTier={userCompany?.tier === "professional"} />
              
              <PricingCard
                tier="enterprise"
                isPopular={false}
                onSignup={() => handlePricingAction("enterprise")}
                isCurrentTier={userCompany?.tier === "enterprise"} />
              
            </div>

            {/* Enterprise+ CTA */}
            <div className="p-8 rounded-2xl bg-gradient-to-r from-primary/10 via-accent/10 to-success/10 border border-primary/20 text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Star className="h-6 w-6 text-primary" />
                <h3 className="text-xl font-bold">Enterprise+ — Custom Solutions</h3>
              </div>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Need unlimited sites, white-labeling, API access, or custom integrations? 
                Contact us for a tailored solution with dedicated CSM support.
              </p>
              <Button
                size="lg"
                variant="outline"
                onClick={() => window.open("https://calendly.com/sorinsafety-id/30min", "_blank")}>
                
                <Calendar className="mr-2 h-5 w-5" />
                Contact Sales
              </Button>
            </div>

            {/* Feature Comparison Table - Grouped by SDAR */}
            <div className="mt-16">
              <h3 className="text-2xl font-bold text-center mb-8">Complete Feature Comparison</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-border">
                      <th className="text-left py-4 px-4 font-semibold">Feature</th>
                      <th className="text-center py-4 px-4 font-semibold">Free</th>
                      <th className="text-center py-4 px-4 font-semibold bg-primary/5">Professional</th>
                      <th className="text-center py-4 px-4 font-semibold">Enterprise</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Platform Limits */}
                    <FeatureGroupHeader title="📊 Platform Limits" />
                    <FeatureComparisonRow feature="Sites Included" free="1" professional="3" enterprise="6" />
                    <FeatureComparisonRow feature="Workers" free="Unlimited" professional="Unlimited" enterprise="Unlimited" />
                    <FeatureComparisonRow feature="AI Conversations/month" free="100" professional="5,000" enterprise="25,000" />
                    <FeatureComparisonRow feature="AI Top-ups" free="3/month" professional="Unlimited" enterprise="Unlimited" />
                    <FeatureComparisonRow feature="Knowledge Base Documents" free="—" professional="15" enterprise="50" />
                    
                    {/* Signal - Hazard Capture */}
                    <FeatureGroupHeader title="📡 Signal — Hazard Capture" />
                    <FeatureComparisonRow feature="Hazard Reporting (Text)" free={true} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Photo Attachment" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Voice Input" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Evidence Quality Score" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Status Tracking" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Shift Check-in (Basic)" free={true} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Fatigue Scale" free={false} professional={false} enterprise={true} />
                    <FeatureComparisonRow feature="Fatigue AI Analysis" free={false} professional={false} enterprise={true} />
                    <FeatureComparisonRow feature="Psychometric Assessment" free={true} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="AI Inspections" free={false} professional="Unlimited" enterprise="Unlimited" />
                    
                    {/* Incident Management */}
                    <FeatureGroupHeader title="🚨 Incident Management" />
                    <FeatureComparisonRow feature="Incident Reporting" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Quick Emergency Flow (Voice)" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="RCA Methods (5-Why, Fishbone, ICAM)" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Action Management" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="SORIN Intelligence Linking" free={false} professional={false} enterprise={true} />
                    <FeatureComparisonRow feature="Predictability Analysis" free={false} professional={false} enterprise={true} />
                    <FeatureComparisonRow feature="HSE Push Alerts" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Manual Entry (Radio/WhatsApp)" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Accident Reporting" free={false} professional={true} enterprise={true} />
                    
                    {/* Diagnose - Risk Intelligence */}
                    <FeatureGroupHeader title="🧠 Diagnose — Risk Intelligence" />
                    <FeatureComparisonRow feature="Basic Analytics Dashboard" free={true} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Trend Analysis" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Network Metrics" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Worker Diagnostics" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="AI Worker Recommendations" free={false} professional={false} enterprise={true} />
                    <FeatureComparisonRow feature="Cluster Diagnostics" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="AI Cluster Recommendations" free={false} professional={false} enterprise={true} />
                    <FeatureComparisonRow feature="Friction Point Mapping" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Risk Pulse Dashboard" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="AI Safety Copilot (Ask Anything)" free={false} professional={false} enterprise={true} />
                    
                    {/* Act - Interventions */}
                    <FeatureGroupHeader title="⚡ Act — Interventions" />
                    <FeatureComparisonRow feature="Supervisor Verification" free={true} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Supervisor Fix Actions" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Action Suggestions (AI)" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Minta Bantuan (Request Help)" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Manual Interventions" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Micro-learning Modules" free={false} professional={false} enterprise={true} />
                    <FeatureComparisonRow feature="Contextual Nudges" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Peer Corroboration" free={false} professional={true} enterprise={true} />
                    
                    {/* Report - Validation & Export */}
                    <FeatureGroupHeader title="📈 Report — Validation & Export" />
                    <FeatureComparisonRow feature="A/B Experiment Platform" free={false} professional={false} enterprise={true} />
                    <FeatureComparisonRow feature="Intervention Effectiveness Tracking" free={false} professional={false} enterprise={true} />
                    <FeatureComparisonRow feature="Worker Results Dashboard" free={false} professional={false} enterprise={true} />
                    <FeatureComparisonRow feature="Data Export" free={false} professional="PDF/CSV/XLS" enterprise="Full Export" />
                    <FeatureComparisonRow feature="Document Upload" free={false} professional={true} enterprise={true} />
                    
                    {/* Gamification & Engagement */}
                    <FeatureGroupHeader title="🎮 Gamification & Engagement" />
                    <FeatureComparisonRow feature="XP Points" free={true} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Badges" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Leaderboard" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Streaks" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="Quests & Challenges" free={false} professional={true} enterprise={true} />
                    
                    {/* Admin & Enterprise */}
                    <FeatureGroupHeader title="🏢 Admin & Enterprise" />
                    <FeatureComparisonRow feature="Admin Dashboard" free={false} professional="Full" enterprise="Enterprise" />
                    <FeatureComparisonRow feature="Multi-site Management" free={false} professional={true} enterprise={true} />
                    <FeatureComparisonRow feature="White Labeling" free={false} professional={false} enterprise="Enterprise+" />
                    <FeatureComparisonRow feature="API Access" free={false} professional={false} enterprise="Enterprise+" />
                    
                    {/* Support & Onboarding */}
                    <FeatureGroupHeader title="🤝 Support & Onboarding" />
                    <FeatureComparisonRow feature="Support Level" free="Community" professional="Email" enterprise="Priority + CSM" />
                    <FeatureComparisonRow feature="Onboarding" free="Self-service" professional="Guided" enterprise="Full Implementation" />
                    <FeatureComparisonRow feature="Training Sessions" free={false} professional="2 sessions" enterprise="Unlimited" />
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-primary/10 via-background to-success/10">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Safety Culture?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join leading mining and construction companies using behavioral science to prevent accidents before they
              happen. See SORIN in action.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                className="h-14 px-8 bg-gradient-to-r from-primary to-success hover:opacity-90 shadow-lg"
                onClick={handleRequestDemo}>
                
                <Calendar className="mr-2 h-5 w-5" />
                Schedule a Demo
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-14 px-8"
                onClick={() => window.open("mailto:support@sorinsafety.com", "_blank")}>
                
                Contact Sales
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border/50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img src={logoFullBlack} alt="SORIN" className="h-6 dark:hidden" />
              <img src={logoFullWhite} alt="SORIN" className="h-6 hidden dark:block" />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              © 2025 SORIN - PT Sinar Inovasi Digital Indonesia. All Rights Reserved.    
            </p>
            <div className="flex items-center gap-6">
              <a href="/privacy-policy" className="text-sm text-muted-foreground hover:text-foreground transition">
                Privacy
              </a>
              <a href="/terms" className="text-sm text-muted-foreground hover:text-foreground transition">
                Terms
              </a>
              <a
                href="mailto:support@sorinsafety.com"
                className="text-sm text-muted-foreground hover:text-foreground transition">
                
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Upgrade Request Dialog for logged-in users */}
      {userCompany &&
      <UpgradeRequestDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        companyId={userCompany.id}
        currentTier={userCompany.tier}
        suggestedTier={selectedUpgradeTier} />

      }
    </div>);

};

// Component Helpers
const StatsCard = ({ label, value, icon }: {label: string;value: string;icon: React.ReactNode;}) =>
<div className="glass-card rounded-2xl p-5 border border-border/50 shadow-soft text-center">
    <div className="p-2 rounded-lg bg-primary/10 text-primary w-fit mx-auto mb-3">{icon}</div>
    <div className="text-2xl font-bold mb-1">{value}</div>
    <div className="text-sm text-muted-foreground">{label}</div>
  </div>;


const ComparisonItem = ({ text, negative }: {text: string;negative?: boolean;}) =>
<li className="flex items-start gap-3">
    {negative ?
  <XCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" /> :

  <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
  }
    <span className={negative ? "text-muted-foreground" : ""}>{text}</span>
  </li>;


const ProcessStep = ({
  step,
  title,
  subtitle,
  description,
  icon,
  color







}: {step: string;title: string;subtitle: string;description: string;icon: React.ReactNode;color: "primary" | "accent" | "warning" | "success";}) => {
  const colorClasses = {
    primary: "bg-primary/10 text-primary border-primary/20",
    accent: "bg-accent/10 text-accent-foreground border-accent/20",
    warning: "bg-warning/10 text-warning border-warning/20",
    success: "bg-success/10 text-success border-success/20"
  };

  return (
    <div className="text-center group">
      <div className="relative inline-block mb-6">
        <div className={`w-20 h-20 rounded-2xl ${colorClasses[color]} flex items-center justify-center border-2 transition-transform group-hover:scale-110`}>
          {icon}
        </div>
        <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-foreground text-background text-sm font-bold flex items-center justify-center">
          {step}
        </div>
      </div>
      <h3 className="font-bold text-xl mb-1">{title}</h3>
      <p className={`text-sm font-medium mb-3 ${color === "primary" ? "text-primary" : color === "accent" ? "text-accent-foreground" : color === "warning" ? "text-warning" : "text-success"}`}>
        {subtitle}
      </p>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>);

};

const FeatureCategory = ({
  icon,
  title,
  features




}: {icon: React.ReactNode;title: string;features: {icon: React.ReactNode;name: string;desc: string;}[];}) =>
<div className="p-6 rounded-2xl bg-card border border-border/50">
    <div className="flex items-center gap-3 mb-6">
      <div className="p-3 rounded-xl bg-primary/10 text-primary">{icon}</div>
      <h3 className="font-bold text-lg">{title}</h3>
    </div>
    <div className="space-y-4">
      {features.map((f, i) =>
    <div key={i} className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-muted text-muted-foreground shrink-0">{f.icon}</div>
          <div>
            <p className="font-medium text-sm">{f.name}</p>
            <p className="text-xs text-muted-foreground">{f.desc}</p>
          </div>
        </div>
    )}
    </div>
  </div>;


const JustCultureCard = ({
  icon,
  title,
  description




}: {icon: React.ReactNode;title: string;description: string;}) =>
<div className="p-6 rounded-2xl bg-card border border-border/50 hover:shadow-medium transition-all">
    <div className="p-3 rounded-xl bg-success/10 text-success w-fit mb-4">{icon}</div>
    <h3 className="font-bold text-lg mb-2">{title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
  </div>;


const ScienceCard = ({
  icon,
  title,
  description,
  stat,
  statLabel






}: {icon: React.ReactNode;title: string;description: string;stat: string;statLabel: string;}) =>
<div className="p-8 rounded-2xl bg-card border border-border/50 hover:shadow-lg transition-all">
    <div className="p-4 rounded-2xl bg-primary/10 text-primary w-fit mb-6">{icon}</div>
    <h3 className="font-bold text-xl mb-3">{title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed mb-6">{description}</p>
    <div className="pt-4 border-t border-border/50">
      <div className="text-3xl font-bold text-primary mb-1">{stat}</div>
      <div className="text-xs text-muted-foreground">{statLabel}</div>
    </div>
  </div>;


const PricingCard = ({
  tier,
  isPopular,
  onSignup,
  isCurrentTier





}: {tier: SubscriptionTier;isPopular: boolean;onSignup: () => void;isCurrentTier?: boolean;}) => {
  const limits = TIER_LIMITS[tier];
  const pricing = TIER_PRICING[tier];
  const name = TIER_NAMES[tier];

  const highlights = {
    free: [
    "1 site included",
    "100 AI conversations/month",
    "Text-based hazard reporting",
    "Basic shift check-in",
    "Psychometric assessment",
    "Supervisor verification",
    "Basic analytics",
    "XP gamification"],

    professional: [
    "3 sites included",
    "5,000 AI conversations/month",
    "Photo & voice reporting",
    "Evidence quality scoring",
    "Action suggestions",
    "AI inspections",
    "Full gamification",
    "Data export (PDF/CSV/XLS)"],

    enterprise: [
    "6 sites included",
    "25,000 AI conversations/month",
    "Everything in Professional",
    "Fatigue analysis",
    "AI recommendations",
    "Micro-learning modules",
    "AI Safety Copilot",
    "A/B experiments"],

    enterprise_plus: []
  };

  return (
    <div className={`relative p-8 rounded-2xl bg-card border-2 ${isPopular ? 'border-primary shadow-lg shadow-primary/10' : 'border-border/50'} ${isCurrentTier ? 'ring-2 ring-success' : ''}`}>
      {isPopular && !isCurrentTier &&
      <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-primary text-primary-foreground text-sm font-medium">
          Most Popular
        </div>
      }
      {isCurrentTier &&
      <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-success text-success-foreground text-sm font-medium">
          Paket Anda
        </div>
      }
      
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold mb-2">{name}</h3>
        <div className="mb-2">
          {pricing.monthly === 0 ?
          <span className="text-4xl font-bold">Free</span> :

          <>
              <span className="text-4xl font-bold">{formatCurrency(pricing.monthly)}</span>
              <span className="text-muted-foreground">/month</span>
            </>
          }
        </div>
        {pricing.monthly > 0 &&
        <p className="text-sm text-muted-foreground">{pricing.usdEquivalent}/month</p>
        }
      </div>

      <ul className="space-y-3 mb-8">
        {highlights[tier].map((feature, index) =>
        <li key={index} className="flex items-start gap-3">
            <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <span className="text-sm">{feature}</span>
          </li>
        )}
      </ul>

      <Button
        className={`w-full ${isPopular && !isCurrentTier ? 'bg-primary hover:bg-primary/90' : ''}`}
        variant={isCurrentTier ? 'secondary' : isPopular ? 'default' : 'outline'}
        onClick={onSignup}
        disabled={isCurrentTier}>
        
        {isCurrentTier ? 'Paket Aktif' : tier === 'free' ? 'Start Free' : 'Get Started'}
        {!isCurrentTier && <ArrowRight className="ml-2 h-4 w-4" />}
      </Button>
    </div>);

};

const FeatureGroupHeader = ({ title }: {title: string;}) =>
<tr className="bg-muted/50">
    <td colSpan={4} className="py-3 px-4 font-semibold text-sm">{title}</td>
  </tr>;


const FeatureComparisonRow = ({
  feature,
  free,
  professional,
  enterprise





}: {feature: string;free: boolean | string;professional: boolean | string;enterprise: boolean | string;}) => {
  const renderValue = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return value ?
      <Check className="h-5 w-5 text-primary mx-auto" /> :

      <X className="h-5 w-5 text-muted-foreground/50 mx-auto" />;

    }
    return <span className="text-sm">{value}</span>;
  };

  return (
    <tr className="border-b border-border/50 hover:bg-muted/30 transition-colors">
      <td className="py-3 px-4 text-sm pl-8">{feature}</td>
      <td className="py-3 px-4 text-center">{renderValue(free)}</td>
      <td className="py-3 px-4 text-center bg-primary/5">{renderValue(professional)}</td>
      <td className="py-3 px-4 text-center">{renderValue(enterprise)}</td>
    </tr>);

};

export default Welcome;